import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MsfSelect } from '@components/select/select';
import { MsfMultiSelect } from '@components/multi-select/multi-select';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { SvgChartComponent, ChartData, ChartConfig } from '@components/svg-chart';
import {  Era5MonthWeatherReportService } from '@6grain/masofa-monolith-api-client/weather-report';
import { MapMonitoringObject } from '@6grain/masofa-monolith-api-client/map';
import {
  ChartDateManager,
  ChartIndicatorManager,
  ChartDataLoadManager,
  ChartBuilderManager,
  PointData,
  PointDataResult, LegendItem,
} from './managers';
import {EraWeatherStationService} from '@6grain/masofa-monolith-api-client/weather';

@Component({
  selector: 'app-all-points-chart-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    MsfSelect,
    MsfMultiSelect,
    DatePickerComponent,
    SvgChartComponent
  ],
  templateUrl: './all-points-chart-modal.component.html',
  styleUrls: ['./all-points-chart-modal.component.scss']
})
export class AllPointsChartModalComponent implements OnInit, OnChanges {
  // Injected services
  private translate = inject(TranslateService);
  private weatherService = inject(EraWeatherStationService);
  private era5MonthWeatherReportService = inject(Era5MonthWeatherReportService);

  // Inputs
  @Input() visible: boolean = false;
  @Input() monitoringPoints: MapMonitoringObject[] | null = [];
  @Input() dataSourceOptions: any[] = [];

  // Outputs
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();

  // Managers
  private dateManager: ChartDateManager;
  private indicatorManager: ChartIndicatorManager;
  private dataLoadManager!: ChartDataLoadManager;
  private chartBuilder: ChartBuilderManager;

  // UI State
  allPointsOptions: PointData[] = [];
  allPointsSelectedPoints: string[] = [];
  allPointsStartDate: Date | null = new Date(new Date().getFullYear(), 0, 1);
  allPointsEndDate: Date | null = new Date(new Date().getFullYear(), 11, 31);
  allPointsDataSource: string = 'era5';
  allPointsWeatherIndicator: string = 'temperatureAverage';
  selectedPointForWeather: PointData | null = null;

  // Weather layer options
  weatherLayerOptions: any[] = [
    {
      names: {
        'en-US': 'Temperature',
        'ru-RU': 'Температура',
        'uz-Latn-UZ': 'Harorat',
      },
      id: 'temperatureAverage'
    },
    {
      names: {
        'en-US': 'Precipitation',
        'ru-RU': 'Осадки',
        'uz-Latn-UZ': "Yog'ingarchilik",
      },
      id: 'precipitation'
    },
    {
      names: {
        'en-US': 'Humidity',
        'ru-RU': 'Влажность',
        'uz-Latn-UZ': 'Namlik',
      },
      id: 'humidity'
    },
    {
      names: {
        'en-US': 'Wind speed',
        'ru-RU': 'Скорость ветра',
        'uz-Latn-UZ': 'Shamol tezligi',
      },
      id: 'windSpeed'
    },
    {
      names: {
        'en-US': 'Solar Radiation',
        'ru-RU': 'Солнечная радиация',
        'uz-Latn-UZ': 'Quyosh nurlanishi',
      },
      id: 'solarRadiation'
    }
  ];
 
  // Chart state
  chartData: ChartData = {labels: [], datasets: []};
  chartConfig: ChartConfig = {};
  isLoading: boolean = false;
  errorMessage: string | null = null;

  // Legend data (stores min/max year per point)
  private pointDataResults: PointDataResult[] = [];

  constructor() {
    // Initialize managers
    this.dateManager = new ChartDateManager();
    this.indicatorManager = new ChartIndicatorManager(this.translate);
    this.chartBuilder = new ChartBuilderManager();
  }

  ngOnInit(): void {
    // Initialize data load manager with injected services
    this.dataLoadManager = new ChartDataLoadManager(
      this.weatherService,
      this.era5MonthWeatherReportService,
      this.dateManager,
      this.indicatorManager
    );

    // Initialize chart config
    this.chartConfig = this.chartBuilder.buildChartConfig(
      this.indicatorManager.getIndicatorUnit(this.allPointsWeatherIndicator)
    );

    this.initializeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.monitoringPoints?.length) {
      this.initializeOptions();
    }
  }


  /**
   * Initialize point options from monitoring points
   */
  private initializeOptions(): void {
    this.allPointsOptions = (this.monitoringPoints || [])
      .map(point => ({
        id: point.id as string,
        name: point.name || this.translate.instant('MONITORING_POINT'),
        lat: point?.pointJson?.lat || 0,
        lng: point?.pointJson?.lon || 0
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    if (this.allPointsOptions.length > 0 && !this.selectedPointForWeather) {
      this.selectedPointForWeather = this.allPointsOptions[0];
    }

  }


  /**
   * Handle close button click
   */
  onClose(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  /**
   * Handle data source change
   */
  onDataSourceChange(): void {

    this.allPointsWeatherIndicator = this.indicatorManager.getDefaultIndicator(this.allPointsDataSource);

    // Update chart config with new unit
    this.chartConfig = this.chartBuilder.buildChartConfig(
      this.indicatorManager.getIndicatorUnit(this.allPointsWeatherIndicator)
    );

    // Reload data
    this.loadChartData();
  }

  /**
   * Handle indicator change
   */
  onIndicatorChange(): void {
    // Update chart config with new unit
    this.chartConfig = this.chartBuilder.buildChartConfig(
      this.indicatorManager.getIndicatorUnit(this.allPointsWeatherIndicator)
    );

    // Reload data
    this.loadChartData();
  }

  /**
   * Handle point selection change
   */
  onPointsSelectionChange(): void {
    this.loadChartData();
  }

  /**
   * Handle date change
   */
  onDateChange(): void {
    this.loadChartData();
  }

  /**
   * Load chart data for all selected points
   */
  loadChartData(): void {
    // Clear previous state
    this.errorMessage = null;
    this.pointDataResults = [];

    // Validate selection
    if (!this.allPointsSelectedPoints || this.allPointsSelectedPoints.length === 0) {
      this.chartData = this.chartBuilder.createEmptyChartData();
      return;
    }

    // Generate labels for the date range
    const labels = this.dateManager.generateMonthLabels(
      this.allPointsStartDate,
      this.allPointsEndDate
    );

    if (labels.length === 0) {
      this.chartData = this.chartBuilder.createEmptyChartData();
      this.errorMessage = this.translate.instant('INVALID_DATE_RANGE');
      return;
    }

    // Get selected point data
    const selectedPoints = this.allPointsSelectedPoints
      .map(id => this.allPointsOptions.find(p => p.id === id))
      .filter((p): p is PointData => p !== undefined);

    if (selectedPoints.length === 0) {
      this.chartData = this.chartBuilder.createEmptyChartData();
      return;
    }

    // Start loading
    this.isLoading = true;

    // Load data for all selected points
    this.dataLoadManager.loadMultiplePointsData(
      selectedPoints,
      this.allPointsStartDate,
      this.allPointsEndDate,
      this.allPointsWeatherIndicator,
      labels,
      this.allPointsDataSource
    ).subscribe({
      next: (results) => {
        this.pointDataResults = results;
        this.chartData = this.chartBuilder.buildChartData(labels, results);
        this.isLoading = false;

        // Check for errors
        const failedPoints = results.filter(r => !r.success);
        if (failedPoints.length > 0 && failedPoints.length === results.length) {
          this.errorMessage = this.translate.instant('NO_DATA_AVAILABLE');
        }
      },
      error: (err) => {
        console.error('Error loading chart data:', err);
        this.chartData = this.chartBuilder.createEmptyChartData();
        this.isLoading = false;
        this.errorMessage = this.translate.instant('ERROR_LOADING_DATA');
      }
    });
  }

  /**
   * Get legend items for display
   */
  getLegendItems(): LegendItem[] {
    return this.chartBuilder.buildLegendItems(this.pointDataResults);
  }

  /**
   * Format legend label
   */
  formatLegend(item: LegendItem): string {
    return this.chartBuilder.formatLegendLabel(item.name, item.minYear, item.maxYear);
  }
}
